import { apiClient } from './api';

export interface Student {
  student_id: number;
  name: string;
  first_name?: string;
  last_name?: string;
  grade_level?: string | null;
  birth_date?: string | null;
  parent_id?: number | null;
  is_public: boolean;
  is_visible: boolean;
  created_at: string;
  updated_at: string;
}

export interface StudentWithStats extends Student {
  artwork_count: number;
  latest_artwork?: string;
  latest_artworks?: Array<{ artwork_id: number; updated_at: string }>;
  favorite_tags: string[];
}

export interface CreateStudentRequest {
  first_name: string;
  last_name: string;
  grade_level?: string;
  birth_date?: string;
  parent_id?: number;
  is_public?: boolean;
  is_visible?: boolean;
}

export interface UpdateStudentRequest {
  first_name?: string;
  last_name?: string;
  grade_level?: string;
  birth_date?: string;
  parent_id?: number;
  is_public?: boolean;
  is_visible?: boolean;
}

export interface StudentFilters {
  grade_level?: string;
  is_public?: boolean;
  is_visible?: boolean;
  has_parent?: boolean;
  search?: string;
  created_after?: string;
  created_before?: string;
}

export interface StudentListResponse {
  students: StudentWithStats[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface StudentStats {
  total_students: number;
  by_grade: Array<{
    grade_level: string;
    count: number;
  }>;
  public_profiles: number;
  with_parent_accounts: number;
  active_this_month: number;
  recent_registrations: Student[];
}

export class StudentService {
  private normalizeStudent(student: any): StudentWithStats {
    const createdAt = student.created_at || '';

    return {
      ...student,
      name: student.name || 'Unknown Student',
      grade_level: null,
      birth_date: null,
      is_public: true,
      is_visible: student.is_visible ?? true,
      created_at: createdAt,
      updated_at: student.updated_at || createdAt,
      artwork_count: student.artwork_count || 0,
      latest_artwork: student.latest_artwork || undefined,
      latest_artworks: student.latest_artworks || [],
      favorite_tags: []
    };
  }

  private normalizePublicStudent(student: any): StudentWithStats {
    // A public response is an authorization boundary. Missing visibility is
    // invalid data, not an invitation to apply the legacy display default.
    if (!student || student.is_visible !== true) {
      throw new Error('Public student response did not confirm visibility');
    }

    return this.normalizeStudent(student);
  }

  // Public endpoint - returns only visible students
  async getVisibleStudents(
    page = 1,
    limit = 20,
    filters?: StudentFilters,
    signal?: AbortSignal,
  ): Promise<StudentListResponse> {
    const offset = (page - 1) * limit;
    const params = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString(),
    });

    if (filters) {
      if (filters.grade_level) {
        params.append('grade_level', filters.grade_level);
      }
      if (filters.search) {
        params.append('search', filters.search);
      }
      if (filters.created_after) {
        params.append('created_after', filters.created_after);
      }
      if (filters.created_before) {
        params.append('created_before', filters.created_before);
      }
    }

    // Call public endpoint that returns only visible students
    const backendResponse = await apiClient.get<{
      message: string;
      data: any[];
      pagination: {
        total: number;
        offset: number;
        limit: number;
        hasMore: boolean;
      };
    }>(`/students?${params}`, { signal });

    // Transform backend response to frontend format
    const currentPage = Math.floor(backendResponse.pagination.offset / backendResponse.pagination.limit) + 1;
    const totalPages = Math.ceil(backendResponse.pagination.total / backendResponse.pagination.limit);

    return {
      students: backendResponse.data.map(student => this.normalizePublicStudent(student)),
      total: backendResponse.pagination.total,
      page: currentPage,
      limit: backendResponse.pagination.limit,
      totalPages: totalPages
    };
  }

  // Admin endpoint - returns all students including hidden ones
  async getAllStudentsForAdmin(
    page = 1,
    limit = 20,
    filters?: StudentFilters
  ): Promise<StudentListResponse> {
    const offset = (page - 1) * limit;
    const params = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString(),
    });

    if (filters) {
      if (filters.grade_level) {
        params.append('grade_level', filters.grade_level);
      }
      if (filters.is_public !== undefined) {
        params.append('is_public', filters.is_public.toString());
      }
      if (filters.is_visible !== undefined) {
        params.append('is_visible', filters.is_visible.toString());
      }
      if (filters.has_parent !== undefined) {
        params.append('has_parent', filters.has_parent.toString());
      }
      if (filters.search) {
        params.append('search', filters.search);
      }
      if (filters.created_after) {
        params.append('created_after', filters.created_after);
      }
      if (filters.created_before) {
        params.append('created_before', filters.created_before);
      }
    }

    // Call admin backend API and transform response to match frontend expectations
    const backendResponse = await apiClient.get<{
      message: string;
      data: any[];
      pagination: {
        total: number;
        offset: number;
        limit: number;
        hasMore: boolean;
      };
    }>(`/admin/students?${params}`);

    // Transform backend response to frontend format
    const currentPage = Math.floor(backendResponse.pagination.offset / backendResponse.pagination.limit) + 1;
    const totalPages = Math.ceil(backendResponse.pagination.total / backendResponse.pagination.limit);

    return {
      students: backendResponse.data.map(student => this.normalizeStudent(student)),
      total: backendResponse.pagination.total,
      page: currentPage,
      limit: backendResponse.pagination.limit,
      totalPages: totalPages
    };
  }

  // Backwards compatibility method - delegates to admin endpoint
  async getStudents(
    page = 1,
    limit = 20,
    filters?: StudentFilters
  ): Promise<StudentListResponse> {
    return this.getAllStudentsForAdmin(page, limit, filters);
  }

  async getPublicStudent(id: number, signal?: AbortSignal): Promise<StudentWithStats> {
    const backendResponse = await apiClient.get<{
      message: string;
      data: any;
    }>(`/students/${id}?includeArtworkCount=true`, { signal });

    // Transform backend response to frontend format
    const student = backendResponse.data;
    return this.normalizePublicStudent(student);
  }

  async createStudent(student: CreateStudentRequest): Promise<Student> {
    // Transform frontend data format to backend format
    const backendRequest = {
      name: `${student.first_name} ${student.last_name}`.trim(),
      parent_id: student.parent_id,
      is_visible: student.is_visible ?? true
    };

    const backendResponse = await apiClient.post<{
      message: string;
      data: any;
    }>('/admin/students', backendRequest);

    // Transform backend response to frontend format
    const createdStudent = backendResponse.data;
    return this.normalizeStudent(createdStudent);
  }

  async updateStudent(id: number, updates: UpdateStudentRequest): Promise<Student> {
    // Transform frontend data format to backend format for updates
    const backendRequest: any = {};
    if (updates.first_name || updates.last_name) {
      const firstName = updates.first_name || '';
      const lastName = updates.last_name || '';
      backendRequest.name = `${firstName} ${lastName}`.trim();
    }
    if (updates.parent_id !== undefined) {
      backendRequest.parent_id = updates.parent_id;
    }
    if (updates.is_visible !== undefined) {
      backendRequest.is_visible = updates.is_visible;
    }

    const backendResponse = await apiClient.put<{
      message: string;
      data: any;
    }>(`/admin/students/${id}`, backendRequest);

    // Transform backend response to frontend format
    const updatedStudent = backendResponse.data;
    return this.normalizeStudent(updatedStudent);
  }

  async deleteStudent(id: number): Promise<void> {
    await apiClient.delete(`/admin/students/${id}`);
  }

  // Student visibility management
  async toggleStudentVisibility(id: number): Promise<{ is_visible: boolean }> {
    return apiClient.put<{ is_visible: boolean }>(`/admin/students/${id}/visibility/toggle`);
  }

  async setStudentVisibility(id: number, isVisible: boolean): Promise<{ is_visible: boolean }> {
    return apiClient.put<{ is_visible: boolean }>(`/admin/students/${id}/visibility`, {
      is_visible: isVisible
    });
  }

  // Search and filtering
  async searchStudents(query: string, limit = 10): Promise<StudentWithStats[]> {
    const response = await this.getStudents(1, limit, { search: query });
    return response.students;
  }

  async getStudentsByGrade(gradeLevel: string): Promise<StudentWithStats[]> {
    const response = await this.getStudents(1, 100, { grade_level: gradeLevel });
    return response.students;
  }

  async getHiddenStudents(page = 1, limit = 20): Promise<StudentListResponse> {
    return this.getAllStudentsForAdmin(page, limit, { is_visible: false });
  }

  async getStudentsWithParents(): Promise<StudentWithStats[]> {
    const response = await this.getStudents(1, 100, { has_parent: true });
    return response.students;
  }

  async getStudentsWithoutParents(): Promise<StudentWithStats[]> {
    const response = await this.getStudents(1, 100, { has_parent: false });
    return response.students;
  }

  async getVisibleStudentArtworks(studentId: number, page = 1, limit = 20, signal?: AbortSignal): Promise<{
    artworks: Array<{
      artwork_id: number;
      student_id?: number | null;
      student_name?: string;
      uploaded_by: number;
      uploaded_at: string;
      updated_at: string;
      tags: string[];
    }>;
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    return apiClient.get(`/students/${studentId}/artworks?${params}`, { signal });
  }

  async getStudentStats(): Promise<StudentStats> {
    return apiClient.get<StudentStats>('/admin/students/stats');
  }

  async getStudentAnalytics(id: number): Promise<{
    student_id: number;
    artwork_count: number;
    first_artwork: string;
    latest_artwork: string;
    favorite_mediums: Array<{
      medium: string;
      count: number;
    }>;
    tag_usage: Array<{
      tag: string;
      count: number;
    }>;
    monthly_activity: Array<{
      month: string;
      artwork_count: number;
    }>;
  }> {
    return apiClient.get(`/students/${id}/analytics`);
  }

  // Grade level management
  async getGradeLevels(): Promise<string[]> {
    return apiClient.get<string[]>('/students/grade-levels');
  }

  async getGradeStats(): Promise<Array<{
    grade_level: string;
    student_count: number;
    artwork_count: number;
    avg_artworks_per_student: number;
  }>> {
    return apiClient.get('/students/grade-stats');
  }

  // Bulk operations
  async bulkCreateStudents(
    students: CreateStudentRequest[]
  ): Promise<{
    created: Student[];
    failed: Array<{ student: CreateStudentRequest; error: string }>;
  }> {
    return apiClient.post('/students/bulk-create', { students });
  }

  async bulkUpdateStudents(
    updates: Array<{ id: number; data: UpdateStudentRequest }>
  ): Promise<Array<{ success: boolean; student?: Student; error?: string }>> {
    const results = await Promise.allSettled(
      updates.map(({ id, data }) => this.updateStudent(id, data))
    );

    return results.map((result) => {
      if (result.status === 'fulfilled') {
        return { success: true, student: result.value };
      } else {
        return {
          success: false,
          error: result.reason?.message || 'Unknown error occurred',
        };
      }
    });
  }

  async bulkDeleteStudents(ids: number[]): Promise<{
    success: number;
    failed: number;
    errors: Array<{ id: number; error: string }>;
  }> {
    const results = await Promise.allSettled(
      ids.map(id => this.deleteStudent(id))
    );

    let successCount = 0;
    const errors: Array<{ id: number; error: string }> = [];

    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        successCount++;
      } else {
        errors.push({
          id: ids[index],
          error: result.reason?.message || 'Unknown error occurred',
        });
      }
    });

    return {
      success: successCount,
      failed: errors.length,
      errors,
    };
  }

  // Privacy and visibility management
  async updatePrivacySettings(id: number, isPublic: boolean): Promise<Student> {
    return this.updateStudent(id, { is_public: isPublic });
  }

  async updateVisibilitySettings(id: number, isVisible: boolean): Promise<Student> {
    return this.updateStudent(id, { is_visible: isVisible });
  }

  async bulkUpdatePrivacy(
    updates: Array<{ id: number; is_public: boolean }>
  ): Promise<Array<{ success: boolean; student?: Student; error?: string }>> {
    return this.bulkUpdateStudents(
      updates.map(({ id, is_public }) => ({ id, data: { is_public } }))
    );
  }

  async bulkUpdateVisibility(
    updates: Array<{ id: number; is_visible: boolean }>
  ): Promise<Array<{ success: boolean; student?: Student; error?: string }>> {
    return this.bulkUpdateStudents(
      updates.map(({ id, is_visible }) => ({ id, data: { is_visible } }))
    );
  }

  // Validation helpers
  validateStudentData(data: CreateStudentRequest): {
    valid: boolean;
    errors: Record<string, string>;
  } {
    const errors: Record<string, string> = {};

    if (!data.first_name || data.first_name.trim().length === 0) {
      errors.first_name = 'First name is required';
    } else if (data.first_name.trim().length > 50) {
      errors.first_name = 'First name must be less than 50 characters';
    }

    if (!data.last_name || data.last_name.trim().length === 0) {
      errors.last_name = 'Last name is required';
    } else if (data.last_name.trim().length > 50) {
      errors.last_name = 'Last name must be less than 50 characters';
    }

    if (data.grade_level && data.grade_level.length > 20) {
      errors.grade_level = 'Grade level must be less than 20 characters';
    }

    if (data.birth_date) {
      const birthDate = new Date(data.birth_date);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();

      if (isNaN(birthDate.getTime())) {
        errors.birth_date = 'Invalid birth date format';
      } else if (age > 18) {
        errors.birth_date = 'Student must be 18 years old or younger';
      } else if (age < 0) {
        errors.birth_date = 'Birth date cannot be in the future';
      }
    }

    return {
      valid: Object.keys(errors).length === 0,
      errors,
    };
  }

  // Utility methods
  getFullName(student: Student): string {
    return student.name || 'Unknown Student';
  }

  getInitials(student: Student): string {
    return student.name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map(part => part[0])
      .join('')
      .toUpperCase() || '?';
  }

  calculateAge(birthDate: string): number | null {
    if (!birthDate) return null;

    const birth = new Date(birthDate);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }

    return age;
  }

  formatGradeLevel(gradeLevel: string | null | undefined): string {
    if (!gradeLevel) return 'Not specified';

    // Handle common grade formats
    const grade = gradeLevel.toLowerCase();
    if (grade === 'k' || grade === 'kindergarten') return 'Kindergarten';
    if (/^\d+$/.test(grade)) return `Grade ${grade}`;
    if (/^grade\s*\d+$/i.test(grade)) return gradeLevel;

    return gradeLevel;
  }

  sortStudentsByName(students: Student[]): Student[] {
    return [...students].sort((a, b) => {
      return a.name.localeCompare(b.name);
    });
  }

  sortStudentsByGrade(students: Student[]): Student[] {
    const gradeOrder = ['k', 'kindergarten', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];

    return [...students].sort((a, b) => {
      const gradeA = a.grade_level?.toLowerCase() || 'z';
      const gradeB = b.grade_level?.toLowerCase() || 'z';

      const indexA = gradeOrder.indexOf(gradeA);
      const indexB = gradeOrder.indexOf(gradeB);

      if (indexA === -1 && indexB === -1) {
        return gradeA.localeCompare(gradeB);
      } else if (indexA === -1) {
        return 1;
      } else if (indexB === -1) {
        return -1;
      } else {
        return indexA - indexB;
      }
    });
  }

  groupStudentsByGrade(students: Student[]): Record<string, Student[]> {
    const groups: Record<string, Student[]> = {};

    students.forEach(student => {
      const grade = this.formatGradeLevel(student.grade_level);
      if (!groups[grade]) {
        groups[grade] = [];
      }
      groups[grade].push(student);
    });

    return groups;
  }

  // Export/Import helpers
  exportStudentsToCSV(students: Student[]): string {
    const headers = ['Name', 'Grade Level', 'Birth Date', 'Is Public', 'Is Visible', 'Created At'];
    const rows = students.map(student => [
      student.name,
      student.grade_level || '',
      student.birth_date || '',
      student.is_public ? 'Yes' : 'No',
      student.is_visible ? 'Yes' : 'No',
      new Date(student.created_at).toLocaleDateString(),
    ]);

    return [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');
  }

  parseCSVToStudents(csvContent: string): {
    students: CreateStudentRequest[];
    errors: Array<{ row: number; errors: Record<string, string> }>;
  } {
    const lines = csvContent.split('\n').filter(line => line.trim());
    // Skip header line

    const students: CreateStudentRequest[] = [];
    const errors: Array<{ row: number; errors: Record<string, string> }> = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.replace(/"/g, '').trim());

      const [firstName, ...rest] = (values[0] || '').split(/\s+/).filter(Boolean);
      const student: CreateStudentRequest = {
        first_name: firstName || '',
        last_name: rest.join(' '),
        grade_level: values[1] || undefined,
        birth_date: values[2] || undefined,
        is_public: values[3]?.toLowerCase() === 'yes',
        is_visible: values[4]?.toLowerCase() === 'yes',
      };

      const validation = this.validateStudentData(student);
      if (!validation.valid) {
        errors.push({ row: i + 1, errors: validation.errors });
      } else {
        students.push(student);
      }
    }

    return { students, errors };
  }
}

export const studentService = new StudentService();
export default studentService;
